import { World, Body, Vec2, Circle, Box, RopeJoint, type Contact, type Fixture, type Joint } from 'planck';
import { BUBBLE_R, BUOYANCY, CANDY_R, CUSHION, CUT_TOL, GRAVITY, OMNOM_R, OUT_MARGIN, SPIKE_T, STAR_R, STEP_DT } from './constants';
import { ASPECT, toWorld, WORLD_H, type Level, type Pt } from './level';

export type Status = 'playing' | 'cleared' | 'lost';
type Tag = { kind: 'candy' } | { kind: 'star'; i: number } | { kind: 'omnom' } | { kind: 'bubble'; i: number } | { kind: 'spike' };

export type Snapshot = {
  status: Status; stars: number; starsTotal: number;
  candy: Pt; candyR: number;
  ropes: { hook: Pt; len: number; cut: boolean; auto?: number; attached: boolean }[];
  omnom: Pt; starPos: (Pt | null)[]; bubbles: { p: Pt; popped: boolean; attached: boolean }[];
  spikes: { a: Pt; b: Pt }[]; cushions: { p: Pt; dir: 'n' | 'e' | 's' | 'w'; puff: number }[];
  inBubble: boolean;
};

export class GameWorld {
  readonly world: World;
  readonly candy: Body;
  status: Status = 'playing';
  stars = 0;
  time = 0;
  private ropes: { hook: Pt; len: number; joint: Joint | null; auto?: number; attached: boolean }[] = [];
  private ground: Body;
  private starBodies: (Body | null)[] = [];
  private bubbleBodies: { body: Body | null; p: Pt; popped: boolean }[] = [];
  private cushions: { p: Pt; dir: 'n' | 'e' | 's' | 'w'; puff: number }[] = [];
  private omnomP: Pt;
  private inBubble = false;
  private pending: (() => void)[] = [];

  constructor(readonly level: Level) {
    this.world = new World({ gravity: new Vec2(0, GRAVITY) });
    const c = toWorld(level.candy);
    this.candy = this.world.createBody({ type: 'dynamic', position: new Vec2(c[0], c[1]), bullet: true, linearDamping: 0.2, angularDamping: 0.5 });
    this.candy.createFixture(new Circle(CANDY_R), { density: 1, friction: 0.3, restitution: 0.2, userData: { kind: 'candy' } as Tag });
    const ground = this.world.createBody({ type: 'static' });
    this.ground = ground;
    for (const h of level.hooks) {
      const p = toWorld(h.p);
      if (h.auto) { this.ropes.push({ hook: p, len: 0, joint: null, auto: (h.radius ?? 0.22) * WORLD_H, attached: false }); continue; }
      const joint = this.world.createJoint(new RopeJoint({ bodyA: ground, bodyB: this.candy, localAnchorA: new Vec2(p[0], p[1]), localAnchorB: new Vec2(0, 0), maxLength: h.len * WORLD_H, collideConnected: true }));
      this.ropes.push({ hook: p, len: h.len * WORLD_H, joint, attached: true });
    }
    this.omnomP = toWorld(level.omnom);
    const om = this.world.createBody({ type: 'static', position: new Vec2(this.omnomP[0], this.omnomP[1]) });
    om.createFixture(new Circle(OMNOM_R), { isSensor: true, userData: { kind: 'omnom' } as Tag });
    level.stars.forEach((s, i) => {
      const p = toWorld(s);
      const b = this.world.createBody({ type: 'static', position: new Vec2(p[0], p[1]) });
      b.createFixture(new Circle(STAR_R), { isSensor: true, userData: { kind: 'star', i } as Tag });
      this.starBodies.push(b);
    });
    (level.bubbles ?? []).forEach((bp, i) => {
      const p = toWorld(bp);
      const b = this.world.createBody({ type: 'static', position: new Vec2(p[0], p[1]) });
      b.createFixture(new Circle(BUBBLE_R), { isSensor: true, userData: { kind: 'bubble', i } as Tag });
      this.bubbleBodies.push({ body: b, p, popped: false });
    });
    for (const s of level.spikes ?? []) {
      const a = toWorld(s.a), b = toWorld(s.b);
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]), ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
      ground.createFixture(new Box(len / 2, SPIKE_T / 2, new Vec2((a[0] + b[0]) / 2, (a[1] + b[1]) / 2), ang), { isSensor: true, userData: { kind: 'spike' } as Tag });
    }
    this.cushions = (level.cushions ?? []).map((c) => ({ p: toWorld(c.p), dir: c.dir, puff: 0 }));
    this.world.on('begin-contact', (c: Contact) => this.onContact(c));
  }

  private tag(f: Fixture): Tag | undefined { return f.getUserData() as Tag | undefined; }
  private onContact(c: Contact) {
    if (this.status !== 'playing') return;
    const [a, b] = [c.getFixtureA(), c.getFixtureB()];
    const ta = this.tag(a), tb = this.tag(b);
    const other = ta?.kind === 'candy' ? tb : tb?.kind === 'candy' ? ta : undefined;
    if (!other) return;
    if (other.kind === 'omnom') this.status = 'cleared';
    else if (other.kind === 'spike') this.status = 'lost';
    else if (other.kind === 'star') {
      const i = other.i; const sb = this.starBodies[i];
      if (sb) { this.stars++; this.starBodies[i] = null; this.pending.push(() => this.world.destroyBody(sb)); }
    } else if (other.kind === 'bubble') {
      const bb = this.bubbleBodies[other.i]!;
      if (!bb.popped && bb.body) { this.inBubble = true; bb.popped = true; const body = bb.body; bb.body = null; this.pending.push(() => this.world.destroyBody(body)); }
    }
  }

  /** 손가락 선분 (a→b, 월드 좌표)이 밧줄과 교차하면 자른다. 잘린 수. */
  cut(a: Pt, b: Pt): number {
    let n = 0;
    const cp = this.candy.getPosition();
    for (const r of this.ropes) {
      if (!r.joint) continue;
      const pts = this.ropePolyline(r, [cp.x, cp.y]);
      for (let i = 1; i < pts.length; i++) {
        if (segDist(pts[i - 1]!, pts[i]!, a, b) <= CUT_TOL) { this.world.destroyJoint(r.joint); r.joint = null; n++; break; }
      }
    }
    return n;
  }

  /** 탭: 거품(사탕에 붙은 것)이면 터뜨림, 쿠션이면 바람. */
  tap(p: Pt): void {
    if (this.status !== 'playing') return;
    const cp = this.candy.getPosition();
    if (this.inBubble && Math.hypot(p[0] - cp.x, p[1] - cp.y) < BUBBLE_R * 1.4) { this.inBubble = false; return; }
    for (const c of this.cushions) {
      if (Math.abs(p[0] - c.p[0]) < CUSHION.w * 1.5 && Math.abs(p[1] - c.p[1]) < CUSHION.h * 3) {
        c.puff = 0.3;
        const d = { n: [0, 1], s: [0, -1], e: [1, 0], w: [-1, 0] }[c.dir];
        const dx = cp.x - c.p[0], dy = cp.y - c.p[1];
        const along = dx * d[0]! + dy * d[1]!, across = Math.abs(dx * d[1]! - dy * d[0]!);
        if (along > 0 && along < CUSHION.range && across < CUSHION.w * 1.2) {
          const k = (1 - along / CUSHION.range) * CUSHION.impulse * this.candy.getMass();
          this.candy.applyLinearImpulse(new Vec2(d[0]! * k, d[1]! * k), this.candy.getWorldCenter(), true);
        }
      }
    }
  }

  step(dt = STEP_DT) {
    if (this.status !== 'playing') return;
    if (this.inBubble) this.candy.applyForceToCenter(new Vec2(0, -GRAVITY * BUOYANCY * this.candy.getMass()), true);
    this.world.step(dt, 8, 3);
    this.time += dt;
    // 자동 고리: 사탕이 반경 안에 들어오면 그 거리로 밧줄이 걸린다(한 번만)
    const cp0 = this.candy.getPosition();
    for (const r of this.ropes) {
      if (r.auto === undefined || r.attached) continue;
      const d = Math.hypot(cp0.x - r.hook[0], cp0.y - r.hook[1]);
      if (d <= r.auto) {
        r.len = d; r.attached = true;
        r.joint = this.world.createJoint(new RopeJoint({ bodyA: this.ground, bodyB: this.candy, localAnchorA: new Vec2(r.hook[0], r.hook[1]), localAnchorB: new Vec2(0, 0), maxLength: d, collideConnected: true }));
      }
    }
    for (const f of this.pending) f(); this.pending = [];
    for (const c of this.cushions) c.puff = Math.max(0, c.puff - dt);
    const p = this.candy.getPosition();
    if (p.y < -OUT_MARGIN || p.x < -OUT_MARGIN || p.x > ASPECT * WORLD_H + OUT_MARGIN || p.y > WORLD_H + OUT_MARGIN * 3) this.status = 'lost';
  }

  /** 밧줄 그리기용 점열: 팽팽하면 직선, 느슨하면 가운데가 처진 곡선. */
  ropePolyline(r: { hook: Pt; len: number }, c: Pt): Pt[] {
    const d = Math.hypot(c[0] - r.hook[0], c[1] - r.hook[1]);
    const sag = Math.max(0, (r.len - d) / 2);
    const mid: Pt = [(r.hook[0] + c[0]) / 2, (r.hook[1] + c[1]) / 2 - sag];
    const out: Pt[] = [];
    for (let i = 0; i <= 8; i++) {
      const t = i / 8;
      out.push([(1 - t) * (1 - t) * r.hook[0] + 2 * (1 - t) * t * mid[0] + t * t * c[0], (1 - t) * (1 - t) * r.hook[1] + 2 * (1 - t) * t * mid[1] + t * t * c[1]]);
    }
    return out;
  }

  snapshot(): Snapshot {
    const cp = this.candy.getPosition();
    return {
      status: this.status, stars: this.stars, starsTotal: this.level.stars.length,
      candy: [cp.x, cp.y], candyR: CANDY_R,
      ropes: this.ropes.map((r) => ({ hook: r.hook, len: r.len, cut: r.attached && !r.joint, auto: r.auto, attached: r.attached })),
      omnom: this.omnomP,
      starPos: this.level.stars.map((s, i) => (this.starBodies[i] ? toWorld(s) : null)),
      bubbles: this.bubbleBodies.map((b) => ({ p: b.p, popped: b.popped, attached: false })),
      spikes: (this.level.spikes ?? []).map((s) => ({ a: toWorld(s.a), b: toWorld(s.b) })),
      cushions: this.cushions, inBubble: this.inBubble,
    };
  }
}

function segDist(a: Pt, b: Pt, c: Pt, d: Pt): number {
  // 두 선분 ab, cd 사이 최소 거리(교차하면 0)
  const o = (p: Pt, q: Pt, r: Pt) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
  const s1 = o(a, b, c), s2 = o(a, b, d), s3 = o(c, d, a), s4 = o(c, d, b);
  if (((s1 > 0) !== (s2 > 0)) && ((s3 > 0) !== (s4 > 0))) return 0;
  const pd = (p: Pt, u: Pt, v: Pt) => { const l2 = (v[0] - u[0]) ** 2 + (v[1] - u[1]) ** 2; let t = l2 ? ((p[0] - u[0]) * (v[0] - u[0]) + (p[1] - u[1]) * (v[1] - u[1])) / l2 : 0; t = Math.max(0, Math.min(1, t)); return Math.hypot(p[0] - (u[0] + t * (v[0] - u[0])), p[1] - (u[1] + t * (v[1] - u[1]))); };
  return Math.min(pd(a, c, d), pd(b, c, d), pd(c, a, b), pd(d, a, b));
}
